import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react'
import {
  chatSendMessageToast,
  chatSendMessageToastAvatar,
  chatSendMessageToastCloseButton,
  chatSendMessageToastComposeMessageContainer,
  chatSendMessageToastConnectionContainer,
  chatSendMessageToastConnectionDetails,
  chatSendMessageToastConnectionDetailsContainer,
  chatSendMessageToastConnectionName,
  chatSendMessageToastConnectionsList,
  chatSendMessageToastConnectionsSelectOption,
  chatSendMessageToastConnectionsSelectOptions,
  chatSendMessageToastContent,
  chatSendMessageToastFooter,
  chatSendMessageToastFooterLabel,
  chatSendMessageToastHeader,
  chatSendMessageToastInput,
  chatSendMessageToastSelectConnectionsContainer,
  chatSendMessageToastSelectConnectionsInput,
  chatSendMessageToastSelectConnectionsInputContainer,
  chatSendMessageToastSubjectsContainer,
  chatSendMessageToastSubmitMessage,
  chatSendMessageToastTag,
  chatSendMessageToastTitle,
} from './ChatSendMessageToast.css'
import { ChatSendMessageToastContext } from '@/components/chat/ChatSendMessageToast/ChatSendMessageToastProvider'
import { ButtonComponent, IconButton, Tag } from '@/components/commons'
import { Avatar } from '@/components/commons/images/Avatar.component'
import { useMutation } from '@/shared/hooks/useMutation'
import { chatStore } from '@/components/chat/ChatStore/ChatStore'
import { UserContext } from '../../../context'
import { LoaderSpinner } from '@/components/ui/LoaderSpinner.component'
import { useQuery } from '@/shared/hooks/useQuery'
import { IUser } from '@/interfaces/user'
import { getUsersByIds, getUserTotalAudience } from '@/services/user'
import Fuse from 'fuse.js'
import { Icon } from '../../../public/assets/v2/images/icons'
import { useRouter } from 'next/router'

const DISMISS_TIMEOUT_DURATION = 5000

export const ChatSendMessageToast = () => {
  const { selectedConnections, isVisible, hideToast } = useContext(
    ChatSendMessageToastContext
  )
  const { user } = useContext(UserContext)
  const { pathname } = useRouter()
  const [sendMessage, { isLoading }] = useMutation({
    mutateMethod: chatStore.sendMessage,
  })
  const [createChannel] = useMutation({
    mutateMethod: chatStore.createChannel,
  })
  const { push } = useRouter()
  const [messageWasSent, setMessageWasSent] = useState(false)
  const [isSelectConnectionsVisible, toggleIsSelectConnectionsVisible] =
    useReducer((state) => !state, false)
  const messageStateRef = useRef<Record<string, string>>({})
  const timeoutFunctionRef = useRef(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const imageInputRef = useRef<HTMLInputElement>(null)
  const inputDefaultValue = useMemo(() => {
    if (!isVisible || selectedConnections.length > 1) return ''
    return messageStateRef.current[selectedConnections[0].id]
  }, [isVisible, selectedConnections])

  useEffect(() => {
    if (isVisible && textareaRef.current && !isSelectConnectionsVisible) {
      textareaRef.current.focus()
      textareaRef.current.setSelectionRange(
        textareaRef.current.value.length,
        textareaRef.current.value.length
      )
    }
  }, [isVisible, isSelectConnectionsVisible])
  const onCreateChannel = useCallback(async () => {
    const channel = await createChannel(
      selectedConnections.map((connection) => connection.id)
    )
    if ('error' in channel) return
    chatStore.selectChannel(channel)
  }, [selectedConnections, createChannel])

  useEffect(() => {
    if (selectedConnections.length) {
      onCreateChannel()
    }
  }, [selectedConnections, user, onCreateChannel])

  const onCloseToast = useCallback(
    (saveMessage = false) => {
      if (saveMessage && selectedConnections.length === 1) {
        messageStateRef.current[selectedConnections[0].id] =
          textareaRef?.current?.value
      } else {
        if (textareaRef?.current) {
          textareaRef.current.value = ''
        }
      }
      chatStore.selectChannel(null)
      hideToast()
      if (timeoutFunctionRef.current) {
        clearTimeout(timeoutFunctionRef.current)
      }
      setMessageWasSent(false)
    },
    [selectedConnections, hideToast]
  )
  const onKeyDown = useCallback(
    async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        e.stopPropagation()

        // Disable lint any rule for next line
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const res = (await sendMessage(textareaRef.current.value)) as any // TODO: need to be fixed but right now we need to many changes to do this, good to refactor

        if (res && 'error' in res) return

        setMessageWasSent(true)

        timeoutFunctionRef.current = setTimeout(
          onCloseToast,
          DISMISS_TIMEOUT_DURATION
        )
      } else if (e.key === 'Escape') {
        onCloseToast(true)
      }
    },
    [onCloseToast, sendMessage]
  )
  const onSubmitFile = useCallback(
    async (e) => {
      if (!chatStore.state.activeChannel) return
      if (e.target.files && e.target.files[0]) {
        const res = await sendMessage(e.target.files[0])
        if ('error' in res) return
        setMessageWasSent(true)
        timeoutFunctionRef.current = setTimeout(
          onCloseToast,
          DISMISS_TIMEOUT_DURATION
        )
      }
    },
    [onCloseToast, sendMessage]
  )
  const onDismissNotice = useCallback(() => {
    onCloseToast()
  }, [onCloseToast])

  if (!isVisible || pathname.includes('inbox')) return null
  return (
    <div
      className={chatSendMessageToast}
      data-message-was-sent={messageWasSent}
    >
      {messageWasSent ? (
        <div className={chatSendMessageToastSubmitMessage}>
          <h2 className="text-18 text-center font-bold m-0 text-primary ">
            Sent Message
          </h2>
          <p className="text-14 text-center m-0 text-tertiary ">
            Go to the inbox page to continue your conversation
          </p>
          <div
            className="flex flex-row gap-6 justify-center"
            style={{ width: '100%' }}
          >
            <ButtonComponent
              iconLeft={<Icon.NotificationIcon />}
              size="xSmall"
              label="Inbox"
              onClick={() => push('/inbox')}
            />
            <ButtonComponent
              variation="secondary"
              size="xSmall"
              label="Dismiss"
              onClick={onDismissNotice}
            />
          </div>
        </div>
      ) : null}
      <div className={chatSendMessageToastHeader}>
        <h2 className={chatSendMessageToastTitle}>New Message</h2>
        <button
          className={chatSendMessageToastCloseButton}
          onClick={() => onCloseToast(true)}
        >
          Close
        </button>
      </div>
      <div className={chatSendMessageToastContent}>
        <ChatSendMessageToastConnectionsSelect
          onClose={toggleIsSelectConnectionsVisible}
          isVisible={isSelectConnectionsVisible}
        />
        <div className={chatSendMessageToastSubjectsContainer}>
          <ChatSendMessageToastSelectedConnectionsList />
          <IconButton
            transparent
            icon="PlusIcon"
            onClick={toggleIsSelectConnectionsVisible}
          />
        </div>
        <div className={chatSendMessageToastComposeMessageContainer}>
          <ChatSendMessageToastConnectionDetails />
          <textarea
            placeholder="Write your message..."
            ref={textareaRef}
            disabled={isLoading}
            defaultValue={inputDefaultValue}
            onKeyDown={onKeyDown}
            className={chatSendMessageToastInput}
          />
          <input
            hidden
            type="file"
            ref={imageInputRef}
            onChange={onSubmitFile}
            accept=".png,.jpg,.svg"
          />
          <input
            hidden
            type="file"
            ref={fileInputRef}
            onChange={onSubmitFile}
            accept="*"
          />
        </div>
        <div className={chatSendMessageToastFooter}>
          <div className="flex flex-row gap-16">
            <IconButton
              color="gray"
              icon="AddImageIcon"
              onClick={() => imageInputRef?.current?.click()}
            />
            <IconButton
              color="gray"
              icon="AttachmentIcon"
              onClick={() => fileInputRef?.current?.click()}
            />
          </div>
          <span className={chatSendMessageToastFooterLabel}>
            {isLoading ? <LoaderSpinner size={20} /> : <>Press Enter to Send</>}
          </span>
        </div>
      </div>
    </div>
  )
}

const ChatSendMessageToastConnectionsSelect = ({ isVisible, onClose }) => {
  const { user } = useContext(UserContext)
  const { selectedConnections, onSelectConnection } = useContext(
    ChatSendMessageToastContext
  )
  const [activeConnectionIndex, setActiveConnectionIndex] = useState<number>(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const [inputValue, setInputValue] = useState('')
  const { data: connections, isLoading } = useQuery<IUser[]>({
    queryMethod: getUsersByIds,
    queryArguments: user.connections,
    useFetchEffect: !!user?.connections?.length,
  })
  const withoutSelected = useMemo(() => {
    const selectedConnectionsIds = selectedConnections.map((c) => c.id)
    return connections.filter((c) => !selectedConnectionsIds.includes(c.id))
  }, [connections, selectedConnections])
  const searchableConnections = useMemo(() => {
    return new Fuse(withoutSelected, {
      shouldSort: true,
      keys: ['firstName', 'lastName', 'username', 'email'],
    })
  }, [withoutSelected])
  const filteredConnections = useMemo(() => {
    if (!inputValue) return withoutSelected
    return searchableConnections.search(inputValue).map((result) => result.item)
  }, [inputValue, withoutSelected, searchableConnections])
  const onSelectOption = useCallback(() => {
    onSelectConnection(filteredConnections[activeConnectionIndex])
    setInputValue('')
    onClose()
  }, [activeConnectionIndex, filteredConnections, onClose, onSelectConnection])
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        setActiveConnectionIndex(
          activeConnectionIndex + 1 === filteredConnections.length
            ? 0
            : activeConnectionIndex + 1
        )
      } else if (e.key === 'ArrowUp') {
        setActiveConnectionIndex(
          activeConnectionIndex - 1 === -1
            ? filteredConnections.length
            : activeConnectionIndex - 1
        )
      } else if (e.key === 'Enter' && filteredConnections.length) {
        onSelectOption()
      } else if (e.key === 'Escape') {
        onClose()
      }
    },
    [filteredConnections, activeConnectionIndex, onClose, onSelectOption]
  )
  useEffect(() => {
    if (isVisible && inputRef.current) {
      inputRef.current.focus()
    }
  }, [isVisible])
  if (!isVisible) return null

  return (
    <div className={chatSendMessageToastSelectConnectionsContainer}>
      <div className={chatSendMessageToastSelectConnectionsInputContainer}>
        <input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={onKeyDown}
          autoFocus
          ref={inputRef}
          placeholder="Search your connections..."
          className={chatSendMessageToastSelectConnectionsInput}
        />
        <IconButton icon="CloseIcon" onClick={onClose} />
      </div>
      <ul className={chatSendMessageToastConnectionsSelectOptions}>
        {isLoading ? (
          <LoaderSpinner size={30} />
        ) : (
          filteredConnections.map((connection, index) => (
            <li
              key={`chat-toast-connection-select-${connection.id}`}
              data-connection-id={connection.id}
              data-is-active={activeConnectionIndex === index}
              onMouseEnter={() => setActiveConnectionIndex(index)}
              onClick={onSelectOption}
              className={chatSendMessageToastConnectionsSelectOption}
            >
              <Avatar
                className="rounded-12"
                image={connection.avatar}
                size={35}
              />
              <span>
                {connection.firstName} {connection.lastName}
              </span>
            </li>
          ))
        )}
      </ul>
    </div>
  )
}

const ChatSendMessageToastConnectionDetails = () => {
  const { selectedConnections } = useContext(ChatSendMessageToastContext)
  const { data: audienceData } = useQuery({
    queryMethod: getUserTotalAudience,
    queryArguments: selectedConnections[0].id,
    useFetchEffect: selectedConnections.length === 1,
  })
  if (selectedConnections.length !== 1) return null
  const connection = selectedConnections[0]
  const totalAudience = audienceData?.totalAudience as number
  return (
    <div className={chatSendMessageToastConnectionDetailsContainer}>
      <Avatar
        className={chatSendMessageToastAvatar}
        image={connection.avatar}
        size={48}
      />
      <div className={chatSendMessageToastConnectionContainer}>
        <span className={chatSendMessageToastConnectionName}>
          {connection.firstName} {connection.lastName}
        </span>
        <span className={chatSendMessageToastConnectionDetails}>
          {connection.contentType?.join?.(', ')}
        </span>

        {totalAudience !== undefined ? (
          <span className={chatSendMessageToastConnectionDetails}>
            {`${totalAudience.toLocaleString()} Total Audience`}
          </span>
        ) : null}
      </div>
    </div>
  )
}

const ChatSendMessageToastSelectedConnectionsList = () => {
  const { selectedConnections, onRemoveConnection } = useContext(
    ChatSendMessageToastContext
  )

  const listRef = useRef<HTMLDivElement>(null)

  return (
    <div ref={listRef} className={chatSendMessageToastConnectionsList}>
      {selectedConnections.map((connection) => (
        <Tag
          className={chatSendMessageToastTag}
          baseColor="white"
          key={`send-message-connection-list-${connection.id}`}
        >
          {connection.firstName} {connection.lastName}
          {selectedConnections.length > 1 ? (
            <IconButton
              icon="CloseIcon"
              onClick={() => onRemoveConnection(connection.id)}
            />
          ) : null}
        </Tag>
      ))}
      {/*<div*/}
      {/*  className={chatSendMessageToastConnectionsListScrollIndicator}*/}
      {/*  style={{ right: `-${scrollIndicatorRightOffset}px` }}*/}
      {/*/>*/}
    </div>
  )
}
